import { useQuery } from '@tanstack/react-query';
import { clubApi } from '../services/apiService';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { TimePeriod } from '../types/api';
import { logger } from '../utils/logger';
import {
  MARKET_PULSE_DISPLAY_MONTHS,
  MARKET_PULSE_FETCH_MONTHS,
} from '../components/marketPulse/marketPulseConstants';

// Query keys for cache management
export const CLUB_SECTION_KEYS = {
  CATEGORY_SALES: 'clubCategorySales',
  MONTHLY_PERFORMANCE: 'clubMonthlyPerformance',
  BRANCH_AVERAGE_PERFORMANCE: 'clubBranchAveragePerformance',
  MARKET_PULSE: 'clubMarketPulse',
};

/** Map TimePeriod → number of months (mirrors apiService.timeRangeToMonths) */
export const CLUB_TIMEFRAME_MONTHS: Record<string, number> = {
  '1month': 1,
  '3months': 3,
  '6months': 6,
  '12months': 12,
};

export function resolveClubFetchMonths(timeRange: TimePeriod): {
  baseMonths: number;
  fetchMonths: number;
} {
  const baseMonths = CLUB_TIMEFRAME_MONTHS[timeRange] ?? 6;
  return { baseMonths, fetchMonths: baseMonths + 1 };
}

/**
 * نبض بازار: پنجرهٔ نمایش ثابت (`MARKET_PULSE_DISPLAY_MONTHS`)، ولی fetch بیشتر:
 * +1 برای MoM قدیمی‌ترین ماهِ پنجره، و +۱۲ برای ماه‌های سال قبل (YoY).
 * `timeRange` نادیده گرفته می‌شود — بازهٔ UI مستقل از TimePeriod کلاب است.
 */
export function resolveMarketPulseFetchMonths(_timeRange?: TimePeriod): {
  baseMonths: number;
  fetchMonths: number;
} {
  return {
    baseMonths: MARKET_PULSE_DISPLAY_MONTHS,
    fetchMonths: MARKET_PULSE_FETCH_MONTHS,
  };
}

/**
 * داده‌های مشترک «جدول عملکرد ماهانه» و «نبض بازار».
 * یک ماه اضافه fetch می‌شود تا رشد ماه‌به‌ماه اولین ماهِ نمایش‌داده‌شده قابل محاسبه باشد.
 */
async function fetchClubSharedPerformanceData(
  branchId: number,
  fetchMonths: number,
  baseMonths: number
) {
  const [dashboardResponse, allRestaurantsResponse, allRestaurantsPartialResponse, allRestaurantsYoYResponse, marketTotalInvoicesResponse] =
    await Promise.all([
      clubApi.getDashboardDataByMonths(branchId, fetchMonths),
      clubApi.getAllRestaurantsDataByMonths(fetchMonths),
      clubApi.getAllRestaurantsPartialLatestByMonths(fetchMonths),
      clubApi.getAllRestaurantsYoYDataByMonths(baseMonths),
      clubApi.getMarketTotalInvoiceCount(),
    ]);

  const dashboardData = dashboardResponse.data || {};
  const marketTotalInvoiceCount =
    marketTotalInvoicesResponse.data?.totalInvoiceCount ?? 0;

  return {
    branchData: dashboardData.branchData || [],
    brandData: dashboardData.brandData || [],
    allRestaurantsData: allRestaurantsResponse.data || [],
    allRestaurantsPartialLatestData: allRestaurantsPartialResponse.data || [],
    allRestaurantsYoYData: allRestaurantsYoYResponse.data || [],
    branchInfo: dashboardData.branchInfo || {},
    baseMonths,
    marketTotalInvoiceCount,
  };
}

/**
 * Hook for the «نبض بازار» (Market Pulse) dashboard.
 * پنجرهٔ نمایش ۱۳ ماه؛ fetch = ۱۳ + ۱ (MoM) + ۱۲ (YoY) = ۲۶ ماه تاریخچه.
 */
export function useMarketPulseData(
  branchId: number | undefined,
  _customTimeRange?: TimePeriod,
  enabled = true,
) {
  // نبض بازار همیشه پنجرهٔ ثابت ۱۳ ماه (+۱ MoM +۱۲ YoY در fetch) دارد
  const { baseMonths, fetchMonths } = resolveMarketPulseFetchMonths();

  return useQuery({
    queryKey: [CLUB_SECTION_KEYS.MARKET_PULSE, branchId, fetchMonths],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');

      try {
        return await fetchClubSharedPerformanceData(branchId, fetchMonths, baseMonths);
      } catch (error) {
        logger.error('Failed to fetch market pulse data', 'ClubSections', error);
        throw error;
      }
    },
    enabled: enabled && !!branchId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Hook to fetch category sales data for ProductCategorySalesTrend component
 */
export function useClubCategorySales(branchId: number | undefined, customTimeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  const timeRange = customTimeRange || apiTimeFrame;

  return useQuery({
    queryKey: [CLUB_SECTION_KEYS.CATEGORY_SALES, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');

      try {
        const response = await clubApi.getDashboardData(branchId, timeRange);
        return response.data?.categorySalesData || null;
      } catch (error) {
        logger.error('Failed to fetch category sales data', 'ClubSections', error);
        return null;
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Hook to fetch monthly performance data for MonthlyPerformanceTable component
 */
export function useClubMonthlyPerformance(branchId: number | undefined, customTimeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  const timeRange = customTimeRange || apiTimeFrame;
  const { baseMonths, fetchMonths } = resolveClubFetchMonths(timeRange);

  return useQuery({
    queryKey: [CLUB_SECTION_KEYS.MONTHLY_PERFORMANCE, branchId, fetchMonths],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');

      try {
        const [sharedData, branchAverageResponse, kpiThresholdsResponse] = await Promise.all([
          fetchClubSharedPerformanceData(branchId, fetchMonths, baseMonths),
          clubApi.getBranchAverageData(branchId, timeRange),
          clubApi.getKpiThresholds(timeRange),
        ]);

        return {
          ...sharedData,
          branchAverageData: branchAverageResponse.data || [],
          kpiThresholds: kpiThresholdsResponse.data || {},
        };
      } catch (error) {
        logger.error('Failed to fetch monthly performance data', 'ClubSections', error);
        return {
          branchData: [],
          brandData: [],
          allRestaurantsData: [],
          branchAverageData: [],
          kpiThresholds: {},
          branchInfo: {},
          baseMonths,
        };
      }
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
